import {ChangeDetectionStrategy, Component, EventEmitter, HostListener, Inject, OnInit, Output} from "@angular/core";
import {PANEL_DATA} from "../../../../commons/angular/services/panel.service";
import {Service} from "../../../../deliveries/models/service.model";
import {PackageModel} from "../../../../commons/models/package.model";
import {TaskModel} from "../../../../tasks/models/task.model";

export enum SplitAssignmentType {
  ROUTE,
  RESOURCE,
  NONE,
}

interface PackageForSplit {
  package: PackageModel;
  selected: boolean;
  originalCount: number;
}

require('./service-split-by-packages.scss');
@Component({
  standalone: false,
  selector: 'app-service-split-by-packages',
  templateUrl: './service-split-by-packages.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ServiceSplitByPackagesComponent implements OnInit {

  // task and service both share the properties necessary for splitting
  public item: Service | TaskModel;
  public sitePackageItemsMap: any;

  @Output() onClose = new EventEmitter();
  @Output() onSplitServiceByPackages = new EventEmitter<{
    packages: PackageModel[],
    optimize: boolean,
    assign: SplitAssignmentType,
  }>();

  serviceId: string;
  selectedPackages: PackageForSplit[] = [];
  optimize: boolean = true;
  selectedTotals: { weight: number, volume: number, pallets: number } = {weight: 0, volume: 0, pallets: 0};
  packages: PackageForSplit[] = [];
  routeTotals: { weight: number, volume: number, pallets: number } = {weight: 0, volume: 0, pallets: 0};
  lastSelectedPackage: { id: string; index: number } = { id: '', index: -1 };
  isShiftPressed: boolean = false;

  @HostListener('keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (event.shiftKey) {
      this.isShiftPressed = true;
    }
  }

  @HostListener('keyup', ['$event'])
  handleKeyUp(event: KeyboardEvent) {
    if (!event.shiftKey) {
      this.isShiftPressed = false;
    }
  }

  constructor(
    @Inject(PANEL_DATA) public data: { item: Service | TaskModel },
    @Inject('splitService') private splitService
  ) {
  }

  ngOnInit() {
    this.sitePackageItemsMap = this.splitService.getSitePackageItemsMap();
    this.item = this.data.item;
    this.serviceId = 'serviceId' in this.item ? this.item.serviceId : this.item.id;
    this._initializePackages();
  }

  onPackageSelect(pkg: PackageForSplit, index: number) {
    pkg.selected = !pkg.selected;

    if (pkg.selected && !pkg.package.count) {
      pkg.package.count = pkg.originalCount;
    }

    if (this.lastSelectedPackage.id && this.isShiftPressed) {
      const previousIndex = this.lastSelectedPackage.index;

      if (previousIndex > -1) {
        const start: number = index > previousIndex ? previousIndex : index;
        const end: number = index > previousIndex ? index : previousIndex;

        for (let i = start; i <= end; i++) {
          this.packages[i].selected = pkg.selected;

          if (this.packages[i].selected && !this.packages[i].package.count) {
            this.packages[i].package.count = this.packages[i].originalCount;
          }
        }
      }
    }

    this.lastSelectedPackage.id = pkg.package.id;
    this.lastSelectedPackage.index = index;
    this._updateSelectedPackages();
  }

  trackByPackageId(index: number, pkg: PackageForSplit): string {
    return pkg.package.id;
  }

  onCountChange(newValue: number, pkg: PackageForSplit, index: number) {
    const pkgCopy: PackageForSplit = {...pkg};
    pkgCopy.package.count = newValue;
    this.packages[index] = pkgCopy;
    this._updateSelectedPackages();
  }

  close() {
    this.onClose.emit();
  }

  split() {
    if (!this.canSplit()) return;
    this.onSplitServiceByPackages.emit({
      packages: this.selectedPackages.map(p => p.package),
      optimize: this.optimize,
      assign: SplitAssignmentType.NONE
    });
    this.onClose.emit();
  }

  isPackageInvalid(pkg: PackageForSplit): boolean {
    return pkg.selected && (
      pkg.package.count > pkg.originalCount ||
      (pkg.originalCount === 1 && this.packages.length === 1) ||
      (pkg.package.count === pkg.originalCount && this.packages.length === 1)
    );
  }

  canSplit() {
    if (this.selectedPackages.length === 0) {
      return false;
    }

    if (this.selectedPackages.some(pkg => pkg.package.count > pkg.originalCount)) {
      return false;
    }

    // Special case if there's a single package - check if originalCount = 1
    if (this.packages.length === 1 &&
      this.selectedPackages.length === 1 &&
      this.selectedPackages[0].originalCount === 1) {
      return false;
    }
    // Special case if there's a single package - check if originalCount = count
    if (this.packages.length === 1 &&
      this.selectedPackages.length === 1 &&
      this.selectedPackages[0].originalCount === this.selectedPackages[0].package.count) {
      return false;
    }

    return true;
  }

  onSplitAndAssignToResource() {
    if (!this.canSplit()) return;
    this.onSplitServiceByPackages.emit({
      packages: this.selectedPackages.map(p => p.package),
      optimize: this.optimize,
      assign: SplitAssignmentType.RESOURCE
    });
    this.onClose.emit();
  }

  onSplitAndAssignToRoute() {
    if (!this.canSplit()) return;
    this.onSplitServiceByPackages.emit({
      packages: this.selectedPackages.map(p => p.package),
      optimize: this.optimize,
      assign: SplitAssignmentType.ROUTE
    });
    this.onClose.emit();
  }

  private _initializePackages() {
    if (this.item?.packageList) {
      this.packages = this.item.packageList[0].items
        .filter(pkg => pkg.id)
        .map((pkg: PackageModel) => ({
          package: pkg,
          selected: false,
          originalCount: pkg.count,
        }));
    }
  }

  private _updateSelectedPackages() {
    this.selectedPackages = this.packages.filter(pkg => pkg.selected);
    this._updateSelectedTotals();
  }

  private _updateSelectedTotals() {
    let weight = 0, volume = 0, pallets = 0;
    const isTotalWeight = this.splitService.getPackageWeightIsTotal();
    const isTotalVolume = this.splitService.getPackageVolumeIsTotal();

    this.selectedPackages.forEach(p => {
      const pkg = p.package;
      weight += pkg.weight || 0;
      volume += (pkg.volume ? pkg.volume : pkg.dimensions?.cube) || 0;
      pallets += pkg.pallets || 0;

      if (!isTotalWeight) {
        weight *= pkg.count;
      }

      if (!isTotalVolume) {
        volume *= pkg.count;
      }
    });
    this.selectedTotals = {weight, volume, pallets};
  }
}
